// utils/syncHelper.ts

import BookedRoom from "../models/BookedRoom";
import Booking from "../models/Booking";
import Order from "../models/Order";
import { BillHistoryResponse } from "../types/bill";
import { errorMessage, sendRequest } from "./api"
import { initAllStores } from "./cache";
import { terminal } from "./user-helper";

/**
 * Synchronizes multiple services (foods, drinks, games, laundries, rooms)
 * with backend and updates IndexedDB cache + timestamps.
 */
export async function syncServices(
    services: string[] = ["drinks", "foods", "games", "laundries", "rooms"],
    lastSyncTime: Record<string, string> = {},
     terminal = "",
) {
    const cache = await initAllStores();

    // 1️⃣ Prepare default lastSyncTime
    const defaultDate = "2024-01-01T00:00:00.000Z";
    for (const service of services) {
        lastSyncTime[service] = lastSyncTime[service] || defaultDate;
    }

    // 2️⃣ Fetch latest data from server
    const url = `/sync/service?terminal=${terminal}`;
    const data = await sendRequest("POST", url, lastSyncTime);

    const { drinks, foods, games, laundries, rooms } = data;
    
    // 3️⃣ Clear and update cache for these services
    await cache.clearAll(services);
    await cache.saveMany({
        foods, drinks, rooms,
        games, laundries,
    });


    // 4️⃣ Save new sync timestamps
    const now = new Date().toISOString();
    const newSyncTime: Record<string, string> = {};
    for (const service of services) newSyncTime[service] = now;

    await cache.saveOne("data", { id: "lastSyncTime", ...newSyncTime });

    return { success: true, updatedAt: now, services };
}


export const syncBillHistory = async (
    unSyncData: Partial<BillHistoryResponse>,
    date?: { start: string, end: string },
    search?: string
): Promise<BillHistoryResponse> => {
    const cache = await initAllStores();
    const availableBills = Object.keys(unSyncData);
    
       const terminalId = terminal();
    try {
        const url = `/sync/bills?terminal=${terminalId}&search=${search}`;
        const bills: Partial<BillHistoryResponse> =
            (await sendRequest("POST", url, { ...unSyncData, date } )) || {};

        // Clear all stores in one go
        await cache.clearAll(availableBills);

        // Save all bills in one go (dictionary: { orders: [...], bookings: [...] })
        await cache.saveMany(bills as Record<string, any[]>);

    } catch (error: any) {
        const status = error?.status ?? 500;
        const err = errorMessage(status);

        if (status === 409) {
            // Duplicate order handling goes here
        } else if (err) {
            // openModal(err.title, err.msg, closeModal, false);
        }
    } finally {
        const bills: Partial<BillHistoryResponse> = {};

        for (const bill of availableBills) {
            // const sortField: keyof BillResponseItem =
            // bill === "bookings" ? "bookingNo" : "orderNo";

            for (const bill of availableBills) {
                if (bill === "orders") {
                    bills.orders = (await cache.getAllBy("orders", { terminal: terminalId }, "orderNo")) as Order[];
                } else if (bill === "bookings") {
                    bills.bookings = (await cache.getAllBy("bookings",  { terminal: terminalId }, "bookingNo")) as Booking[];
                } else if (bill === "bookedRooms") {
                    bills.bookedRooms = (await cache.getAllBy("bookedRooms", { terminal: terminalId })) as BookedRoom[];
                }
            }
        }

        return bills as BillHistoryResponse;
    }
};

